import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const isMob = () => window.innerWidth <= 768;
const fmt = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// ── Introducer commission view ───────────────────────────────────────────────
function IntroducerCommissions({ data, mobile }) {
  const s = data.summary || {};
  const SCOPE_LABEL = { company: 'Company-wide', manager: 'Your Team', adviser: 'Your Earnings' };

  const AllocationRow = ({ rows, emptyMsg }) => (
    rows.length === 0
      ? <p style={{ color: '#a0aec0', fontSize: 13, padding: '8px 0' }}>{emptyMsg}</p>
      : rows.map((r, i) => (
        <div key={r.id} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 0', borderBottom: i < rows.length - 1 ? '1px solid #f3f4f6' : 'none',
          flexWrap: 'wrap', gap: 8,
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#0e1b2c' }}>{r.client_name}</div>
            <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>
              {r.submitted_by ? `Submitted by ${r.submitted_by} · ` : ''}
              {r.paid_at ? `Paid ${fmtDate(r.paid_at)}` : `Deal created ${fmtDate(r.deal_created_at)}`}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: r.status === 'paid' ? '#10b981' : r.status === 'payable' ? '#6366f1' : '#f59e0b' }}>
              {r.status !== 'paid' ? '~' : ''}{fmt(r.amount)}
            </div>
            <div style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 600, marginTop: 4, display: 'inline-block',
              background: r.status === 'paid' ? '#d1fae5' : r.status === 'payable' ? '#eef2ff' : '#fef3c7',
              color: r.status === 'paid' ? '#065f46' : r.status === 'payable' ? '#6366f1' : '#92400e',
            }}>
              {r.status === 'paid' ? '✓ Paid' : r.status === 'payable' ? 'Awaiting payment' : 'In pipeline'}
            </div>
          </div>
        </div>
      ))
  );

  return (
    <div style={{ padding: mobile ? '16px' : '32px 40px', maxWidth: 1100 }}>
      <p style={{ margin: '0 0 24px', fontSize: 13, color: '#718096' }}>
        {SCOPE_LABEL[data.scope] || 'Your'} commission overview — year to date
      </p>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3,1fr)', gap: 16, marginBottom: 32 }}>
        {[
          { label: 'In Pipeline',      value: fmt(s.pipelineTotal), sub: `${s.pipelineCount || 0} deals`,    accent: '#f59e0b', bg: '#fffbeb' },
          { label: 'Awaiting Payment', value: fmt(s.payableTotal),  sub: `${s.payableCount || 0} deals`,    accent: '#6366f1', bg: '#eef2ff' },
          { label: 'Total Paid (YTD)', value: fmt(s.paidTotal),     sub: `${s.paidCount || 0} payments`,    accent: '#10b981', bg: '#f0fdf4' },
        ].map(c => (
          <div key={c.label} style={{
            background: c.bg, borderRadius: 10, padding: '24px',
            border: `1px solid ${c.accent}33`, borderTop: `4px solid ${c.accent}`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#0e1b2c' }}>{c.value}</div>
            <div style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Awaiting payment */}
      {(data.payable || []).length > 0 && (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', padding: '24px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>
            ⏳ Awaiting Payment ({data.payable.length})
          </h2>
          <AllocationRow rows={data.payable} emptyMsg="" />
        </div>
      )}

      {/* Payment history */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', padding: '24px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>✓ Payment History</h2>
        <AllocationRow rows={data.paidHistory || []} emptyMsg="No payments received yet." />
      </div>

      {/* Pipeline */}
      {(data.pipeline || []).length > 0 && (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>
            📋 In Pipeline ({data.pipeline.length})
          </h2>
          <AllocationRow rows={data.pipeline} emptyMsg="" />
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function CommissionDashboard({ noLayout = false } = {}) {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [intPay, setIntPay]   = useState(null);
  const [partnerRefs, setPartnerRefs] = useState([]);
  const [markingPaid, setMarkingPaid] = useState(null);

  const markPaid = async (r) => {
    if (!window.confirm(`Mark ${fmt(r.due)} as paid to ${r.introducer_name}? This sets today as the paid date on all their completed, unpaid deals.`)) return;
    setMarkingPaid(r.introducer_user_id);
    try {
      const res = await fetch(`/api/commissions/introducer-payments/${r.introducer_user_id}/mark-paid`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!res.ok) alert(data.error || 'Failed to mark as paid');
      else loadDashboard();
    } catch (err) {
      console.error(err);
      alert('Failed to mark as paid');
    } finally {
      setMarkingPaid(null);
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [mobile, setMobile]   = useState(isMob());

  useEffect(() => {
    const onResize = () => setMobile(isMob());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const res = await fetch('/api/commissions/dashboard', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const json = await res.json();
      setData(json);
      if (json?.role !== 'introducer') {
        const ipRes = await fetch('/api/commissions/introducer-payments', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (ipRes.ok) setIntPay(await ipRes.json());
        const prRes = await fetch('/api/partners/referrals', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (prRes.ok) { const pr = await prRes.json(); if (Array.isArray(pr)) setPartnerRefs(pr); }
      }
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const ml = mobile ? 0 : 70;
  const pt = mobile ? 64 : 0;

  if (loading) return noLayout ? <div style={{padding:40,color:'#718096'}}>Loading…</div> : <div style={{display:'flex',minHeight:'100vh',background:'#F7FAFC'}}><Sidebar /><div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'#718096'}}>Loading…</div></div>;
  if (error) return noLayout ? <div style={{padding:24,color:'#e74c3c'}}>{error}</div> : <div style={{display:'flex',minHeight:'100vh',background:'#F7FAFC'}}><Sidebar /><div style={{flex:1,padding:24,color:'#e74c3c'}}>{error}</div></div>;

  // Header back button destination depends on role
  const backPath = data?.role === 'introducer' ? '/introducer-dashboard' : '/dashboard';

  const exportCsv = async () => {
    try {
      const res = await fetch('/api/exports/commissions', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) { alert('Export failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'commissions.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export failed'); }
  };

  const _inner = (
    <div style={{ flex: 1, minWidth: 0 }}>

        {/* Header */}
        <div style={{
          background: '#fff', borderBottom: '1px solid #E2E8F0',
          padding: mobile ? '16px' : '20px 40px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: mobile ? 18 : 24, fontWeight: 700, color: '#0e1b2c' }}>Introducer Commission</h1>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#718096' }}>What you owe your introducer network</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={exportCsv} title="Download the full commission report as CSV" style={{
              padding: '8px 16px', background: '#fff', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, fontSize: 13,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}>⬇ Export CSV</button>
            <button onClick={() => navigate(backPath)} style={{
              padding: '8px 16px', background: '#10b981', color: '#fff',
              border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}>← Dashboard</button>
          </div>
        </div>

        {/* Introducer view */}
        {data?.role === 'introducer' && <IntroducerCommissions data={data} mobile={mobile} />}

        {/* Broker view */}
        {data?.role !== 'introducer' && (
          <div style={{ padding: mobile ? '16px' : '32px 40px', maxWidth: 1200 }}>
            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
              {[
                { label: 'Paid to Introducers (YTD)', value: fmt(data?.summary?.paidTotal),  detail: `${data?.summary?.paidCount || 0} payments this year`, accent: '#10b981' },
                { label: 'Due Now',              value: fmt(data?.summary?.payableTotal),      detail: `${data?.summary?.payableCount || 0} completed, unpaid`, accent: '#ef4444' },
                { label: 'In Pipeline',          value: fmt(intPay?.totals?.pending || 0),     detail: 'commission in deals in progress', accent: '#6366f1' },
              ].map(c => (
                <div key={c.label} style={{
                  background: '#fff', borderRadius: 10, padding: mobile ? '16px' : '24px',
                  border: '1px solid #E2E8F0', borderTop: `4px solid ${c.accent}`,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{c.label}</div>
                  <div style={{ fontSize: mobile ? 22 : 28, fontWeight: 700, color: '#0e1b2c' }}>{c.value}</div>
                  <div style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>{c.detail}</div>
                </div>
              ))}
            </div>

            {/* Introducer payments tally */}
            {intPay && intPay.introducers.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', padding: '24px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>Introducer Payments</h2>
                <p style={{ margin: '0 0 16px', fontSize: 12.5, color: '#718096' }}>
                  What each introducer is owed. <strong>Pending</strong> = deal still in progress · <strong>Due</strong> = deal completed, awaiting payment · <strong>Paid</strong> = settled (set the paid date on the deal).
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#a0aec0', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        <th style={{ padding: '8px 8px 8px 0' }}>Introducer</th>
                        <th style={{ padding: 8, textAlign: 'right' }}>Deals</th>
                        <th style={{ padding: 8, textAlign: 'right' }}>Pending</th>
                        <th style={{ padding: 8, textAlign: 'right' }}>Due for payment</th>
                        <th style={{ padding: 8, textAlign: 'right' }}>Paid</th>
                        <th style={{ padding: 8 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {intPay.introducers.map(r => (
                        <tr key={r.introducer_user_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 8px 10px 0' }}>
                            <div style={{ fontWeight: 600, color: '#0e1b2c' }}>{r.introducer_name}</div>
                            <div style={{ fontSize: 11.5, color: '#a0aec0' }}>{r.introducer_email}</div>
                          </td>
                          <td style={{ padding: 8, textAlign: 'right', color: '#4b5a6e' }}>{r.total_deals}</td>
                          <td style={{ padding: 8, textAlign: 'right', color: '#f59e0b', fontWeight: 600 }}>{fmt(r.pending)}</td>
                          <td style={{ padding: 8, textAlign: 'right', color: '#ef4444', fontWeight: 700 }}>{fmt(r.due)}</td>
                          <td style={{ padding: 8, textAlign: 'right', color: '#10b981', fontWeight: 600 }}>{fmt(r.paid)}</td>
                          <td style={{ padding: 8, textAlign: 'right' }}>
                            {parseFloat(r.due) > 0 && (
                              <button
                                onClick={() => markPaid(r)}
                                disabled={markingPaid === r.introducer_user_id}
                                style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: markingPaid === r.introducer_user_id ? '#a7f3d0' : '#10b981', border: 'none', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >
                                {markingPaid === r.introducer_user_id ? 'Marking…' : '✓ Mark paid'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                        <td style={{ padding: '10px 8px 10px 0', color: '#0e1b2c' }}>Total</td>
                        <td />
                        <td style={{ padding: 8, textAlign: 'right', color: '#f59e0b' }}>{fmt(intPay.totals.pending)}</td>
                        <td style={{ padding: 8, textAlign: 'right', color: '#ef4444' }}>{fmt(intPay.totals.due)}</td>
                        <td style={{ padding: 8, textAlign: 'right', color: '#10b981' }}>{fmt(intPay.totals.paid)}</td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tier split (company/manager/adviser) moved to the introducer's own pages (D2):
                the broker pays the introducing firm a lump sum and sees cash figures only. */}

            {/* Pipeline deals */}
            {(data?.pipeline || []).length > 0 && (
              <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', padding: '24px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>Pipeline Deals ({data.pipeline.length})</h2>
                {data.pipeline.map((d, i) => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < data.pipeline.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#0e1b2c' }}>{d.client_name}</div>
                        {d.reference && <span style={{ fontSize:11, fontWeight:600, color:'#10b981', background:'#d1fae5', padding:'1px 7px', borderRadius:9999 }}>{d.reference}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#718096' }}>{d.introducer_name}</div>
                    </div>
                    <div style={{ fontWeight: 700, color: '#f59e0b' }}>{fmt(d.actual_fee)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Completed deals */}
            {(data?.completed || []).length > 0 && (
              <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>Completed Deals ({data.completed.length})</h2>
                {data.completed.map((d, i) => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < data.completed.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#0e1b2c' }}>{d.client_name}</div>
                        {d.reference && <span style={{ fontSize:11, fontWeight:600, color:'#10b981', background:'#d1fae5', padding:'1px 7px', borderRadius:9999 }}>{d.reference}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#718096' }}>{d.introducer_name}</div>
                    </div>
                    <div style={{ fontWeight: 700, color: '#10b981' }}>{fmt(d.actual_fee)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Partner referral commission (surfaced 4 Jul 2026) */}
            {partnerRefs.length > 0 && (() => {
              const sum = (f) => partnerRefs.filter(f).reduce((t, r) => t + (parseFloat(r.commission_amount) || 0), 0);
              const paidTot = sum(r => r.status === 'paid');
              const dueTot  = sum(r => r.status === 'completed');
              const pipeTot = sum(r => ['pending', 'accepted'].includes(r.status));
              const introTot = partnerRefs.filter(r => ['completed', 'paid'].includes(r.status)).reduce((t, r) => t + (parseFloat(r.introducer_amount) || 0), 0);
              return (
                <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', padding: '24px', marginTop: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>🤝 Partner Referral Commission</h2>
                    <button onClick={() => navigate('/referrals')} style={{ border: '1px solid #E2E8F0', background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#3730a3', cursor: 'pointer' }}>View all →</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3,1fr)', gap: 12 }}>
                    {[['Received (paid)', paidTot, '#10b981'], ['Due now', dueTot, '#f59e0b'], ['In progress', pipeTot, '#6366f1']].map(([label, v, col]) => (
                      <div key={label} style={{ background: '#F7FAFC', borderRadius: 8, padding: '14px 16px' }}>
                        <div style={{ fontSize: 12, color: '#718096' }}>{label}</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: col }}>{fmt(v)}</div>
                      </div>
                    ))}
                  </div>
                  {introTot > 0 && (
                    <div style={{ marginTop: 12, fontSize: 12, color: '#718096' }}>
                      of which {fmt(introTot)} is due to introducers as revenue share
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

    </div>
  );
  if (noLayout) return _inner;
  return <div style={{display:'flex',minHeight:'100vh',background:'#F7FAFC'}}><Sidebar />{_inner}</div>;
}
